import { BettingStructure, GameType } from './game-types';
import { PokerVariant } from './poker-variant';
import { PokerCategory } from './types';

/**
 * Comprehensive information about a poker game type
 */
export interface GameTypeInfo {
  name: string;
  category: PokerCategory;
  bettingStructure: BettingStructure;
  maxPlayers: number;
  reshuffleRequired: boolean;
  variants: PokerVariant[];
  isMixed: boolean;
  capped?: boolean;
  isBombPot?: boolean;
  boardCount?: number; // Number of boards (1 for single board, 2 for double board)
  // For mixed games that use a betting cap measured in big blinds
  bettingCapInBB?: number;
}

/**
 * A comprehensive unified registry of all poker game types and variants
 */
export const GameRegistry: Record<GameType, GameTypeInfo> = {
  // Standard Holdem games
  [GameType.NO_LIMIT_HOLDEM]: {
    name: GameType.NO_LIMIT_HOLDEM,
    category: PokerCategory.Holdem,
    bettingStructure: BettingStructure.NO_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.NoLimitTexasHoldem],
    isMixed: false,
    capped: false,
  },
  [GameType.NO_LIMIT_HOLDEM_CAPPED]: {
    name: GameType.NO_LIMIT_HOLDEM_CAPPED,
    category: PokerCategory.Holdem,
    bettingStructure: BettingStructure.NO_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.NoLimitTexasHoldemCapped],
    isMixed: false,
    capped: true,
  },
  [GameType.NO_LIMIT_SHORTDECK]: {
    name: GameType.NO_LIMIT_SHORTDECK,
    category: PokerCategory.Holdem,
    bettingStructure: BettingStructure.NO_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.NoLimitShortDeckHoldem],
    isMixed: false,
    capped: false,
  },
  [GameType.LIMIT_HOLDEM]: {
    name: GameType.LIMIT_HOLDEM,
    category: PokerCategory.Holdem,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.FixedLimitTexasHoldem],
    isMixed: false,
    capped: false,
  },

  // Standard Omaha games
  [GameType.PLO]: {
    name: GameType.PLO,
    category: PokerCategory.Omaha,
    bettingStructure: BettingStructure.POT_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.PotLimitOmaha],
    isMixed: false,
    capped: false,
  },
  [GameType.PLO_DOUBLE_BOARD]: {
    name: GameType.PLO_DOUBLE_BOARD,
    category: PokerCategory.Omaha,
    bettingStructure: BettingStructure.POT_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.PotLimitDoubleBoardOmaha],
    isMixed: false,
    capped: false,
    boardCount: 2,
  },
  [GameType.PLO_CAPPED]: {
    name: GameType.PLO_CAPPED,
    category: PokerCategory.Omaha,
    bettingStructure: BettingStructure.POT_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.PotLimitOmahaCapped],
    isMixed: false,
    capped: true,
  },
  [GameType.PLO_8B]: {
    name: GameType.PLO_8B,
    category: PokerCategory.Omaha,
    bettingStructure: BettingStructure.POT_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.PotLimitOmahaEightOrBetter],
    isMixed: false,
    capped: false,
  },
  [GameType.PLO_8B_CAPPED]: {
    name: GameType.PLO_8B_CAPPED,
    category: PokerCategory.Omaha,
    bettingStructure: BettingStructure.POT_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.PotLimitOmahaEightOrBetterCapped],
    isMixed: false,
    capped: true,
  },
  [GameType.PLO5]: {
    name: GameType.PLO5,
    category: PokerCategory.Omaha,
    bettingStructure: BettingStructure.POT_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.PotLimitFiveCardOmaha],
    isMixed: false,
    capped: false,
  },
  [GameType.PLO5_DOUBLE_BOARD]: {
    name: GameType.PLO5_DOUBLE_BOARD,
    category: PokerCategory.Omaha,
    bettingStructure: BettingStructure.POT_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.PotLimitFiveCardDoubleBoardOmaha],
    isMixed: false,
    capped: false,
    boardCount: 2,
  },
  [GameType.PLO5_CAPPED]: {
    name: GameType.PLO5_CAPPED,
    category: PokerCategory.Omaha,
    bettingStructure: BettingStructure.POT_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.PotLimitFiveCardOmahaCapped],
    isMixed: false,
    capped: true,
  },
  [GameType.BIG_O]: {
    name: GameType.BIG_O,
    category: PokerCategory.Omaha,
    bettingStructure: BettingStructure.POT_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.PotLimitFiveCardOmahaEightOrBetter],
    isMixed: false,
    capped: false,
  },
  [GameType.BIG_O_CAPPED]: {
    name: GameType.BIG_O_CAPPED,
    category: PokerCategory.Omaha,
    bettingStructure: BettingStructure.POT_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.PotLimitFiveCardOmahaEightOrBetterCapped],
    isMixed: false,
    capped: true,
  },
  [GameType.PLO6]: {
    name: GameType.PLO6,
    category: PokerCategory.Omaha,
    bettingStructure: BettingStructure.POT_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.PotLimitSixCardOmaha],
    isMixed: false,
    capped: false,
  },
  [GameType.PLO6_CAPPED]: {
    name: GameType.PLO6_CAPPED,
    category: PokerCategory.Omaha,
    bettingStructure: BettingStructure.POT_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.PotLimitSixCardOmahaCapped],
    isMixed: false,
    capped: true,
  },
  [GameType.PLO6_DOUBLE_BOARD]: {
    name: GameType.PLO6_DOUBLE_BOARD,
    category: PokerCategory.Omaha,
    bettingStructure: BettingStructure.POT_LIMIT,
    maxPlayers: 6,
    reshuffleRequired: false,
    variants: [PokerVariant.PotLimitSixCardDoubleBoardOmaha],
    isMixed: false,
    capped: false,
    boardCount: 2,
  },
  [GameType.PLO6_8B]: {
    name: GameType.PLO6_8B,
    category: PokerCategory.Omaha,
    bettingStructure: BettingStructure.POT_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.PotLimitSixCardOmahaEightOrBetter],
    isMixed: false,
    capped: false,
  },
  [GameType.PLO6_8B_CAPPED]: {
    name: GameType.PLO6_8B_CAPPED,
    category: PokerCategory.Omaha,
    bettingStructure: BettingStructure.POT_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.PotLimitSixCardOmahaEightOrBetterCapped],
    isMixed: false,
    capped: true,
  },
  [GameType.NO_LIMIT_OMAHA_HIGH_LOW]: {
    name: GameType.NO_LIMIT_OMAHA_HIGH_LOW,
    category: PokerCategory.Omaha,
    bettingStructure: BettingStructure.NO_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.NoLimitOmahaEightOrBetter],
    isMixed: false,
    capped: true,
  },
  [GameType.LIMIT_OMAHA_HIGH]: {
    name: GameType.LIMIT_OMAHA_HIGH,
    category: PokerCategory.Omaha,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.FixedLimitOmahaHigh],
    isMixed: false,
    capped: false,
  },
  [GameType.LIMIT_OMAHA_DOUBLE_BOARD]: {
    name: GameType.LIMIT_OMAHA_DOUBLE_BOARD,
    category: PokerCategory.Omaha,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.FixedLimitDoubleBoardOmaha],
    isMixed: false,
    capped: false,
    boardCount: 2,
  },
  [GameType.LIMIT_BIG_O]: {
    name: GameType.LIMIT_BIG_O,
    category: PokerCategory.Omaha,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.FixedLimitFiveCardOmahaEightOrBetter],
    isMixed: false,
    capped: false,
  },
  [GameType.LIMIT_FIVE_CARD_DOUBLE_BOARD_OMAHA]: {
    name: GameType.LIMIT_FIVE_CARD_DOUBLE_BOARD_OMAHA,
    category: PokerCategory.Omaha,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.FixedLimitFiveCardDoubleBoardOmaha],
    isMixed: false,
    capped: false,
    boardCount: 2,
  },
  [GameType.LIMIT_OMAHA_8B]: {
    name: GameType.LIMIT_OMAHA_8B,
    category: PokerCategory.Omaha,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.FixedLimitOmahaEightOrBetter],
    isMixed: false,
    capped: false,
  },

  // Standard Stud games
  [GameType.SEVEN_CARD_STUD]: {
    name: GameType.SEVEN_CARD_STUD,
    category: PokerCategory.Stud,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: true,
    variants: [PokerVariant.FixedLimitSevenCardStud],
    isMixed: false,
    capped: false,
  },
  [GameType.STUD_8B]: {
    name: GameType.STUD_8B,
    category: PokerCategory.Stud,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: true,
    variants: [PokerVariant.FixedLimitStudEightOrBetter],
    isMixed: false,
    capped: false,
  },
  [GameType.STUD_HI_LO_REGULAR]: {
    name: GameType.STUD_HI_LO_REGULAR,
    category: PokerCategory.Stud,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: true,
    variants: [PokerVariant.FixedLimitStudHighLowRegular],
    isMixed: false,
    capped: false,
  },
  [GameType.RAZZ]: {
    name: GameType.RAZZ,
    category: PokerCategory.Stud,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: true,
    variants: [PokerVariant.FixedLimitRazz],
    isMixed: false,
    capped: false,
  },
  [GameType.TWO_SEVEN_RAZZ]: {
    name: GameType.TWO_SEVEN_RAZZ,
    category: PokerCategory.Stud,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: true,
    variants: [PokerVariant.FixedLimitDeuceToSevenRazz],
    isMixed: false,
    capped: false,
  },
  [GameType.BADUGI]: {
    name: GameType.BADUGI,
    category: PokerCategory.Draw,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 7,
    reshuffleRequired: true,
    variants: [PokerVariant.FixedLimitBadugi],
    isMixed: false,
    capped: false,
  },
  [GameType.BADEUCY]: {
    name: GameType.BADEUCY,
    category: PokerCategory.Draw,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 7,
    reshuffleRequired: true,
    variants: [PokerVariant.FixedLimitBadeucy],
    isMixed: false,
    capped: false,
  },
  [GameType.BADACEY]: {
    name: GameType.BADACEY,
    category: PokerCategory.Draw,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 7,
    reshuffleRequired: true,
    variants: [PokerVariant.FixedLimitBadacey],
    isMixed: false,
    capped: false,
  },
  [GameType.A5_TRIPLE_DRAW]: {
    name: GameType.A5_TRIPLE_DRAW,
    category: PokerCategory.Draw,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 7,
    reshuffleRequired: true,
    variants: [PokerVariant.FixedLimitAceToFiveLowballTripleDraw],
    isMixed: false,
    capped: false,
  },
  [GameType.TWO_SEVEN_TRIPLE_DRAW]: {
    name: GameType.TWO_SEVEN_TRIPLE_DRAW,
    category: PokerCategory.Draw,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 7,
    reshuffleRequired: true,
    variants: [PokerVariant.FixedLimitDeuceToSevenLowballTripleDraw],
    isMixed: false,
    capped: false,
  },
  [GameType.ARCHIE_66]: {
    name: GameType.ARCHIE_66,
    category: PokerCategory.Draw,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 7,
    reshuffleRequired: true,
    variants: [PokerVariant.FixedLimitArchieSixesHighEightOrBetter],
    isMixed: false,
    capped: false,
  },
  [GameType.ARCHIE_99]: {
    name: GameType.ARCHIE_99,
    category: PokerCategory.Draw,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 7,
    reshuffleRequired: true,
    variants: [PokerVariant.FixedLimitArchieNinesHighEightOrBetter],
    isMixed: false,
    capped: false,
  },
  [GameType.DRAMADUGI]: {
    name: GameType.DRAMADUGI,
    category: PokerCategory.Draw,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 7,
    reshuffleRequired: true,
    variants: [PokerVariant.FixedLimitDramahaBadugi],
    isMixed: false,
    capped: false,
  },
  [GameType.DRAMAHA_HIGH]: {
    name: GameType.DRAMAHA_HIGH,
    category: PokerCategory.Draw,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 7,
    reshuffleRequired: true,
    variants: [PokerVariant.FixedLimitDramahaHigh],
    isMixed: false,
    capped: false,
  },
  [GameType.DRAMAHA_TWO_SEVEN]: {
    name: GameType.DRAMAHA_TWO_SEVEN,
    category: PokerCategory.Draw,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 7,
    reshuffleRequired: true,
    variants: [PokerVariant.FixedLimitDeuceToSevenDramaha],
    isMixed: false,
    capped: false,
  },
  [GameType.NL_TWO_SEVEN_SINGLE_DRAW]: {
    name: GameType.NL_TWO_SEVEN_SINGLE_DRAW,
    category: PokerCategory.Draw,
    bettingStructure: BettingStructure.NO_LIMIT,
    maxPlayers: 7,
    reshuffleRequired: true,
    variants: [PokerVariant.NoLimitDeuceToSevenLowballSingleDraw],
    isMixed: false,
    capped: false,
  },
  [GameType.NL_TWO_SEVEN_SINGLE_DRAW_CAPPED]: {
    name: GameType.NL_TWO_SEVEN_SINGLE_DRAW_CAPPED,
    category: PokerCategory.Draw,
    bettingStructure: BettingStructure.NO_LIMIT,
    maxPlayers: 7,
    reshuffleRequired: true,
    variants: [PokerVariant.NoLimitDeuceToSevenLowballSingleDrawCapped],
    isMixed: false,
    capped: true,
  },

  // Mixed Games
  [GameType.AGAME_MIX]: {
    name: GameType.AGAME_MIX,
    category: PokerCategory.Mixed,
    bettingStructure: BettingStructure.MIXED_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [
      PokerVariant.NoLimitTexasHoldem,
      PokerVariant.PotLimitOmaha,
      PokerVariant.FixedLimitDramahaHigh,
      PokerVariant.NoLimitTexasHoldem,
      PokerVariant.PotLimitFiveCardOmaha,
      PokerVariant.PotLimitSixCardOmahaEightOrBetter,
    ],
    isMixed: true,
    bettingCapInBB: 40,
  },
  [GameType.HEROS_MIX]: {
    name: GameType.HEROS_MIX,
    category: PokerCategory.Mixed,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 8, // Min of all variants
    reshuffleRequired: true, // If any variant requires reshuffling
    variants: [
      PokerVariant.FixedLimitTexasHoldem,
      PokerVariant.FixedLimitStudEightOrBetter,
      PokerVariant.FixedLimitRazz,
      PokerVariant.FixedLimitOmahaEightOrBetter,
      PokerVariant.FixedLimitSevenCardStud,
    ],
    isMixed: true,
    bettingCapInBB: 40,
  },
  [GameType.OE_MIX]: {
    name: GameType.OE_MIX,
    category: PokerCategory.Mixed,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: true,
    variants: [PokerVariant.FixedLimitOmahaEightOrBetter, PokerVariant.FixedLimitStudEightOrBetter],
    isMixed: true,
    bettingCapInBB: 40,
  },
  [GameType.TRIPLE_DRAW_MIX]: {
    name: GameType.TRIPLE_DRAW_MIX,
    category: PokerCategory.Mixed,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 7,
    reshuffleRequired: true,
    variants: [
      PokerVariant.FixedLimitDeuceToSevenLowballTripleDraw,
      PokerVariant.FixedLimitAceToFiveLowballTripleDraw,
      PokerVariant.FixedLimitBadugi,
    ],
    isMixed: true,
    bettingCapInBB: 40,
  },
  [GameType.SUPER_TRIPLE_DRAW_MIX]: {
    name: GameType.SUPER_TRIPLE_DRAW_MIX,
    category: PokerCategory.Mixed,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 7,
    reshuffleRequired: true,
    variants: [
      PokerVariant.FixedLimitAceToFiveLowballTripleDraw,
      PokerVariant.FixedLimitDeuceToSevenLowballTripleDraw,
      PokerVariant.FixedLimitBadacey,
      PokerVariant.FixedLimitBadugi,
      PokerVariant.FixedLimitBadeucy,
    ],
    isMixed: true,
    bettingCapInBB: 40,
  },
  [GameType.DRAMAHA_MIX]: {
    name: GameType.DRAMAHA_MIX,
    category: PokerCategory.Mixed,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 7,
    reshuffleRequired: true,
    variants: [
      PokerVariant.FixedLimitDramahaHigh,
      PokerVariant.FixedLimitDeuceToSevenDramaha,
      PokerVariant.FixedLimitDramahaBadugi,
    ],
    isMixed: true,
    bettingCapInBB: 40,
  },
  [GameType.EIGHT_GAME_MIX]: {
    name: GameType.EIGHT_GAME_MIX,
    category: PokerCategory.Mixed,
    bettingStructure: BettingStructure.MIXED_LIMIT,
    maxPlayers: 7, // Limited by the draw games
    reshuffleRequired: true,
    variants: [
      PokerVariant.FixedLimitDeuceToSevenLowballTripleDraw,
      PokerVariant.NoLimitTexasHoldemCapped,
      PokerVariant.FixedLimitSevenCardStud,
      PokerVariant.FixedLimitOmahaEightOrBetter,
      PokerVariant.FixedLimitRazz,
      PokerVariant.PotLimitOmahaCapped,
      PokerVariant.FixedLimitTexasHoldem,
      PokerVariant.FixedLimitStudEightOrBetter,
    ],
    isMixed: true,
    bettingCapInBB: 40,
  },
  [GameType.EIGHT_GAME_MIX_UNCAPPED]: {
    name: GameType.EIGHT_GAME_MIX_UNCAPPED,
    category: PokerCategory.Mixed,
    bettingStructure: BettingStructure.MIXED_LIMIT,
    maxPlayers: 7, // Limited by the draw games
    reshuffleRequired: true,
    variants: [
      PokerVariant.FixedLimitDeuceToSevenLowballTripleDraw,
      PokerVariant.NoLimitTexasHoldem,
      PokerVariant.FixedLimitSevenCardStud,
      PokerVariant.FixedLimitOmahaEightOrBetter,
      PokerVariant.FixedLimitRazz,
      PokerVariant.PotLimitOmaha,
      PokerVariant.FixedLimitTexasHoldem,
      PokerVariant.FixedLimitStudEightOrBetter,
    ],
    isMixed: true,
    capped: false,
  },
  [GameType.FUN_MIX]: {
    name: GameType.FUN_MIX,
    category: PokerCategory.Mixed,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 7,
    reshuffleRequired: true,
    variants: [
      PokerVariant.FixedLimitAceToFiveLowballTripleDraw,
      PokerVariant.FixedLimitDeuceToSevenRazz,
      PokerVariant.FixedLimitOmahaEightOrBetter,
      PokerVariant.FixedLimitBadeucy,
      PokerVariant.FixedLimitBadugi,
      PokerVariant.FixedLimitDeuceToSevenLowballTripleDraw,
      PokerVariant.FixedLimitStudEightOrBetter,
    ],
    isMixed: true,
    bettingCapInBB: 40,
  },
  [GameType.CIRCUS_MIX]: {
    name: GameType.CIRCUS_MIX,
    category: PokerCategory.Mixed,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 7,
    reshuffleRequired: true,
    variants: [
      PokerVariant.FixedLimitBadeucy,
      PokerVariant.FixedLimitDoubleBoardOmaha,
      PokerVariant.FixedLimitDramahaHigh,
      PokerVariant.FixedLimitStudEightOrBetter,
      PokerVariant.FixedLimitBadacey,
      PokerVariant.FixedLimitBadugi,
      PokerVariant.FixedLimitDeuceToSevenDramaha,
      PokerVariant.FixedLimitStudHighLowRegular,
    ],
    isMixed: true,
    bettingCapInBB: 40,
  },
  [GameType.SUPER_CIRCUS_MIX]: {
    name: GameType.SUPER_CIRCUS_MIX,
    category: PokerCategory.Mixed,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 7,
    reshuffleRequired: true,
    variants: [
      PokerVariant.FixedLimitBadeucy,
      PokerVariant.FixedLimitDoubleBoardOmaha,
      PokerVariant.FixedLimitDramahaHigh,
      PokerVariant.FixedLimitStudEightOrBetter,
      PokerVariant.FixedLimitBadacey,
      PokerVariant.FixedLimitBadugi,
      PokerVariant.FixedLimitDeuceToSevenDramaha,
      PokerVariant.FixedLimitStudHighLowRegular,
      PokerVariant.FixedLimitArchieSixesHighEightOrBetter,
      PokerVariant.FixedLimitOmahaEightOrBetter,
      PokerVariant.FixedLimitDramahaBadugi,
      PokerVariant.FixedLimitDeuceToSevenRazz,
    ],
    isMixed: true,
    bettingCapInBB: 40,
  },
  [GameType.STANDARD_MIX]: {
    name: GameType.STANDARD_MIX,
    category: PokerCategory.Mixed,
    bettingStructure: BettingStructure.MIXED_LIMIT,
    maxPlayers: 7,
    reshuffleRequired: true,
    variants: [
      PokerVariant.PotLimitFiveCardOmahaEightOrBetterCapped,
      PokerVariant.FixedLimitBadugi,
      PokerVariant.FixedLimitBadeucy,
      PokerVariant.FixedLimitStudEightOrBetter,
      PokerVariant.FixedLimitDeuceToSevenDramaha,
      PokerVariant.PotLimitOmahaCapped,
      PokerVariant.FixedLimitBadacey,
      PokerVariant.FixedLimitOmahaEightOrBetter,
      PokerVariant.FixedLimitDeuceToSevenLowballTripleDraw,
      PokerVariant.FixedLimitRazz,
    ],
    isMixed: true,
    bettingCapInBB: 40,
  },
  [GameType.BIG_BET_MIX]: {
    name: GameType.BIG_BET_MIX,
    category: PokerCategory.Mixed,
    bettingStructure: BettingStructure.MIXED_LIMIT,
    maxPlayers: 7,
    reshuffleRequired: true,
    variants: [
      PokerVariant.NoLimitDeuceToSevenLowballSingleDraw,
      PokerVariant.PotLimitOmaha,
      PokerVariant.PotLimitFiveCardOmahaEightOrBetter,
      PokerVariant.NoLimitTexasHoldem,
      PokerVariant.PotLimitFiveCardOmaha,
    ],
    isMixed: true,
    bettingCapInBB: 40,
  },
  [GameType.BIG_BET_MIX_CAPPED]: {
    name: GameType.BIG_BET_MIX_CAPPED,
    category: PokerCategory.Mixed,
    bettingStructure: BettingStructure.MIXED_LIMIT,
    maxPlayers: 7,
    reshuffleRequired: true,
    variants: [
      PokerVariant.NoLimitDeuceToSevenLowballSingleDrawCapped,
      PokerVariant.PotLimitOmahaCapped,
      PokerVariant.PotLimitFiveCardOmahaEightOrBetterCapped,
      PokerVariant.NoLimitTexasHoldemCapped,
      PokerVariant.PotLimitFiveCardOmahaCapped,
    ],
    isMixed: true,
    capped: true,
    bettingCapInBB: 40,
  },
  [GameType.HYBRID_MIX]: {
    name: GameType.HYBRID_MIX,
    category: PokerCategory.Mixed,
    bettingStructure: BettingStructure.MIXED_LIMIT,
    maxPlayers: 7,
    reshuffleRequired: true,
    variants: [
      PokerVariant.NoLimitTexasHoldem,
      PokerVariant.FixedLimitSevenCardStud,
      PokerVariant.PotLimitFiveCardOmahaEightOrBetterCapped,
      PokerVariant.FixedLimitBadugi,
      PokerVariant.NoLimitDeuceToSevenLowballSingleDrawCapped,
      PokerVariant.FixedLimitRazz,
      PokerVariant.NoLimitTexasHoldemCapped,
      PokerVariant.FixedLimitOmahaEightOrBetter,
      PokerVariant.PotLimitFiveCardOmahaCapped,
      PokerVariant.FixedLimitStudEightOrBetter,
      PokerVariant.PotLimitOmaha,
      PokerVariant.FixedLimitDeuceToSevenRazz,
      PokerVariant.PotLimitOmahaCapped,
      PokerVariant.FixedLimitDeuceToSevenLowballTripleDraw,
    ],
    isMixed: true,
    bettingCapInBB: 40,
  },
  [GameType.DIRTY_DOZEN_MIX]: {
    name: GameType.DIRTY_DOZEN_MIX,
    category: PokerCategory.Mixed,
    bettingStructure: BettingStructure.MIXED_LIMIT,
    maxPlayers: 7,
    reshuffleRequired: true,
    variants: [
      PokerVariant.FixedLimitBadeucy,
      PokerVariant.NoLimitTexasHoldemCapped,
      PokerVariant.FixedLimitBadugi,
      PokerVariant.FixedLimitStudEightOrBetter,
      PokerVariant.FixedLimitDeuceToSevenLowballTripleDraw,
      PokerVariant.PotLimitOmahaCapped,
      PokerVariant.FixedLimitBadacey,
      PokerVariant.FixedLimitRazz,
      PokerVariant.NoLimitDeuceToSevenLowballSingleDrawCapped,
      PokerVariant.FixedLimitOmahaEightOrBetter,
      PokerVariant.FixedLimitTexasHoldem,
      PokerVariant.FixedLimitSevenCardStud,
    ],
    isMixed: true,
    bettingCapInBB: 40,
  },
  [GameType.CHOP_SHOP_MIX]: {
    name: GameType.CHOP_SHOP_MIX,
    category: PokerCategory.Mixed,
    bettingStructure: BettingStructure.MIXED_LIMIT,
    maxPlayers: 7,
    reshuffleRequired: true,
    variants: [
      PokerVariant.FixedLimitBadeucy,
      PokerVariant.FixedLimitOmahaEightOrBetter,
      PokerVariant.PotLimitFiveCardOmahaEightOrBetterCapped,
      PokerVariant.FixedLimitStudEightOrBetter,
      PokerVariant.FixedLimitDeuceToSevenDramaha,
      PokerVariant.PotLimitOmahaEightOrBetterCapped,
      PokerVariant.FixedLimitBadacey,
      PokerVariant.FixedLimitStudHighLowRegular,
      PokerVariant.FixedLimitArchieSixesHighEightOrBetter,
      PokerVariant.PotLimitSixCardOmahaEightOrBetterCapped,
      PokerVariant.FixedLimitDramahaBadugi,
      PokerVariant.FixedLimitDeuceToSevenRazz,
    ],
    isMixed: true,
    bettingCapInBB: 40,
  },
  [GameType.TORSE_MIX]: {
    name: 'T.O.R.S.E. Mix',
    category: PokerCategory.Mixed,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 7,
    reshuffleRequired: true,
    variants: [
      PokerVariant.FixedLimitDeuceToSevenLowballTripleDraw,
      PokerVariant.FixedLimitOmahaEightOrBetter,
      PokerVariant.FixedLimitRazz,
      PokerVariant.FixedLimitSevenCardStud,
      PokerVariant.FixedLimitStudEightOrBetter,
    ],
    isMixed: true,
    bettingCapInBB: 40,
  },
  [GameType.TOE_MIX]: {
    name: 'T.O.E. Mix',
    category: PokerCategory.Mixed,
    bettingStructure: BettingStructure.FIXED_LIMIT,
    maxPlayers: 7,
    reshuffleRequired: true,
    variants: [
      PokerVariant.FixedLimitDeuceToSevenLowballTripleDraw,
      PokerVariant.FixedLimitOmahaEightOrBetter,
      PokerVariant.FixedLimitStudEightOrBetter,
    ],
    isMixed: true,
    bettingCapInBB: 40,
  },
  [GameType.PLO_FUSION]: {
    name: GameType.PLO_FUSION,
    category: PokerCategory.Mixed,
    bettingStructure: BettingStructure.MIXED_LIMIT,
    maxPlayers: 6,
    reshuffleRequired: false,
    variants: [
      PokerVariant.PotLimitOmaha,
      PokerVariant.PotLimitFiveCardOmahaEightOrBetter,
      PokerVariant.PotLimitSixCardOmaha,
      PokerVariant.PotLimitOmahaEightOrBetter,
      PokerVariant.PotLimitFiveCardOmaha,
      PokerVariant.PotLimitSixCardOmahaEightOrBetter,
    ],
    isMixed: true,
    bettingCapInBB: 40,
  },
  [GameType.NLH_PLO_MIX]: {
    name: GameType.NLH_PLO_MIX,
    category: PokerCategory.Mixed,
    bettingStructure: BettingStructure.MIXED_LIMIT,
    maxPlayers: 6,
    reshuffleRequired: false,
    variants: [PokerVariant.NoLimitTexasHoldem, PokerVariant.PotLimitOmaha],
    isMixed: true,
    capped: false,
  },
  [GameType.PLO_MIX]: {
    name: GameType.PLO_MIX,
    category: PokerCategory.Mixed,
    bettingStructure: BettingStructure.POT_LIMIT,
    maxPlayers: 6,
    reshuffleRequired: false,
    variants: [PokerVariant.PotLimitOmaha, PokerVariant.PotLimitFiveCardOmaha, PokerVariant.PotLimitSixCardOmaha],
    isMixed: true,
    bettingCapInBB: 40,
  },
  [GameType.BLOOD_MIX]: {
    name: GameType.BLOOD_MIX,
    category: PokerCategory.Mixed,
    bettingStructure: BettingStructure.MIXED_LIMIT,
    maxPlayers: 6,
    reshuffleRequired: true,
    variants: [
      PokerVariant.FixedLimitBadeucy,
      PokerVariant.PotLimitOmahaEightOrBetterCapped,
      PokerVariant.FixedLimitRazz,
      PokerVariant.NoLimitDeuceToSevenLowballSingleDrawCapped,
      PokerVariant.FixedLimitOmahaEightOrBetter,
      PokerVariant.NoLimitTexasHoldemCapped,
      PokerVariant.FixedLimitSevenCardStud,
      PokerVariant.PotLimitFiveCardOmahaCapped,
      PokerVariant.FixedLimitBadugi,
      PokerVariant.PotLimitFiveCardOmahaEightOrBetterCapped,
      PokerVariant.FixedLimitDeuceToSevenRazz,
      PokerVariant.PotLimitOmahaCapped,
      PokerVariant.FixedLimitDeuceToSevenLowballTripleDraw,
      PokerVariant.NoLimitTexasHoldemCapped,
      PokerVariant.FixedLimitStudEightOrBetter,
    ],
    isMixed: true,
    bettingCapInBB: 40,
  },
  [GameType.ARIA_MIX]: {
    name: GameType.ARIA_MIX,
    category: PokerCategory.Mixed,
    bettingStructure: BettingStructure.MIXED_LIMIT,
    maxPlayers: 6,
    reshuffleRequired: true,
    variants: [
      PokerVariant.FixedLimitOmahaEightOrBetter,
      PokerVariant.NoLimitTexasHoldemCapped,
      PokerVariant.FixedLimitBadugi,
      PokerVariant.PotLimitFiveCardOmahaEightOrBetterCapped,
      PokerVariant.FixedLimitRazz,
      PokerVariant.PotLimitOmahaEightOrBetterCapped,
      PokerVariant.FixedLimitSevenCardStud,
      PokerVariant.NoLimitDeuceToSevenLowballSingleDrawCapped,
      PokerVariant.FixedLimitTexasHoldem,
      PokerVariant.PotLimitOmahaCapped,
      PokerVariant.FixedLimitStudEightOrBetter,
    ],
    isMixed: true,
    bettingCapInBB: 100,
  },

  // Bomb Pot games
  [GameType.NL_BOMB_POT]: {
    name: GameType.NL_BOMB_POT,
    category: PokerCategory.Holdem,
    bettingStructure: BettingStructure.NO_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.NoLimitTexasHoldemBombPot],
    isMixed: false,
    capped: false,
    isBombPot: true,
  },
  [GameType.NL_DOUBLE_BOARD_BOMB_POT]: {
    name: GameType.NL_DOUBLE_BOARD_BOMB_POT,
    category: PokerCategory.Holdem,
    bettingStructure: BettingStructure.NO_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.NoLimitTexasHoldemDoubleBoardBombPot],
    isMixed: false,
    capped: false,
    isBombPot: true,
    boardCount: 2,
  },
  [GameType.PLO_BOMB_POT]: {
    name: GameType.PLO_BOMB_POT,
    category: PokerCategory.Omaha,
    bettingStructure: BettingStructure.POT_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.PotLimitOmahaBombPot],
    isMixed: false,
    capped: false,
    isBombPot: true,
  },
  [GameType.PLO_DOUBLE_BOARD_BOMB_POT]: {
    name: GameType.PLO_DOUBLE_BOARD_BOMB_POT,
    category: PokerCategory.Omaha,
    bettingStructure: BettingStructure.POT_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.PotLimitOmahaDoubleBoardBombPot],
    isMixed: false,
    capped: false,
    isBombPot: true,
    boardCount: 2,
  },
  [GameType.PLO5_BOMB_POT]: {
    name: GameType.PLO5_BOMB_POT,
    category: PokerCategory.Omaha,
    bettingStructure: BettingStructure.POT_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.PotLimitFiveCardOmahaBombPot],
    isMixed: false,
    capped: false,
    isBombPot: true,
  },
  [GameType.PLO5_DOUBLE_BOARD_BOMB_POT]: {
    name: GameType.PLO5_DOUBLE_BOARD_BOMB_POT,
    category: PokerCategory.Omaha,
    bettingStructure: BettingStructure.POT_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.PotLimitFiveCardOmahaDoubleBoardBombPot],
    isMixed: false,
    capped: false,
    isBombPot: true,
    boardCount: 2,
  },
  [GameType.PLO6_BOMB_POT]: {
    name: GameType.PLO6_BOMB_POT,
    category: PokerCategory.Omaha,
    bettingStructure: BettingStructure.POT_LIMIT,
    maxPlayers: 8,
    reshuffleRequired: false,
    variants: [PokerVariant.PotLimitSixCardOmahaBombPot],
    isMixed: false,
    capped: false,
    isBombPot: true,
  },
  [GameType.PLO6_DOUBLE_BOARD_BOMB_POT]: {
    name: GameType.PLO6_DOUBLE_BOARD_BOMB_POT,
    category: PokerCategory.Omaha,
    bettingStructure: BettingStructure.POT_LIMIT,
    maxPlayers: 6,
    reshuffleRequired: false,
    variants: [PokerVariant.PotLimitSixCardOmahaDoubleBoardBombPot],
    isMixed: false,
    capped: false,
    isBombPot: true,
    boardCount: 2,
  },

  // OFC (Open Face Chinese) games
  [GameType.OFC_STANDARD]: {
    name: GameType.OFC_STANDARD,
    category: PokerCategory.Chinese,
    bettingStructure: BettingStructure.POINTS,
    maxPlayers: 3,
    reshuffleRequired: false,
    variants: [PokerVariant.OFCStandard],
    isMixed: false,
    capped: false,
  },
  [GameType.OFC_PINEAPPLE]: {
    name: GameType.OFC_PINEAPPLE,
    category: PokerCategory.Chinese,
    bettingStructure: BettingStructure.POINTS,
    maxPlayers: 3,
    reshuffleRequired: false,
    variants: [PokerVariant.OFCPineapple],
    isMixed: false,
    capped: false,
  },
  [GameType.OFC_PINEAPPLE_PROGRESSIVE]: {
    name: GameType.OFC_PINEAPPLE_PROGRESSIVE,
    category: PokerCategory.Chinese,
    bettingStructure: BettingStructure.POINTS,
    maxPlayers: 3,
    reshuffleRequired: false,
    variants: [PokerVariant.OFCPineappleProgressive],
    isMixed: false,
    capped: false,
  },
  [GameType.OFC_PINEAPPLE_27]: {
    name: GameType.OFC_PINEAPPLE_27,
    category: PokerCategory.Chinese,
    bettingStructure: BettingStructure.POINTS,
    maxPlayers: 3,
    reshuffleRequired: false,
    variants: [PokerVariant.OFCPineapple27],
    isMixed: false,
    capped: false,
  },
};

/**
 * Get game type info by game type
 */
export const getGameTypeInfo = (gameType: GameType): GameTypeInfo => {
  return GameRegistry[gameType];
};

/**
 * Check if a game is a mixed game
 */
export const isMixedGame = (gameType: GameType): boolean => {
  return GameRegistry[gameType]?.isMixed || false;
};

/**
 * Check if a category is OFC (Open Face Chinese)
 */
export const isOFCCategory = (category: PokerCategory): boolean => {
  return category === PokerCategory.Chinese;
};

/**
 * Check if a game is an OFC (Open Face Chinese) game
 */
export const isOFCGame = (gameType: GameType): boolean => {
  return isOFCCategory(GameRegistry[gameType]?.category);
};

/**
 * Get all variants for a specific game type
 */
export const getVariantsForGameType = (gameType: GameType): PokerVariant[] => {
  return GameRegistry[gameType]?.variants || [];
};

/**
 * Get all game types that include a specific variant
 */
export const getGameTypesForVariant = (variant: PokerVariant): GameType[] => {
  return Object.entries(GameRegistry)
    .filter(([_, info]) => info.variants.includes(variant))
    .map(([gameType]) => gameType as GameType);
};

export const getGameInfoForVariant = (variant: string): GameTypeInfo => {
  // Convert string to PokerVariant enum value
  const pokerVariant = variant as PokerVariant;

  const gameTypes = getGameTypesForVariant(pokerVariant);
  if (gameTypes.length === 0) {
    throw new Error(`No game types found for variant ${variant}`);
  }
  return GameRegistry[gameTypes[0]];
};

/**
 * Checks if a given poker variant is a bomb pot variant.
 * @param variant The PokerVariant to check.
 * @returns True if the variant is a bomb pot, false otherwise.
 */
export const isBombPotVariant = (variant: string): boolean => {
  const gameInfo = getGameInfoForVariant(variant);
  return gameInfo?.isBombPot === true;
};

/**
 * Gets the board count for a given poker variant.
 * @param variant The PokerVariant to check.
 * @returns The number of boards (defaults to 1 if not specified).
 */
export const getBoardCountForVariant = (variant: string): number => {
  const gameInfo = getGameInfoForVariant(variant);
  return gameInfo?.boardCount ?? 1;
};

/**
 * Checks if a given poker variant is a split pot game (high/low).
 * @param variant The PokerVariant to check.
 * @returns True if the variant is a split pot game, false otherwise.
 */
export const isSplitPotGame = (variant: string): boolean => {
  if (!variant) return false;

  return (
    variant.includes('EightOrBetter') ||
    variant.includes('HighLowRegular') ||
    variant.includes('Badeucy') ||
    variant.includes('Badacey') ||
    variant.includes('ArchieNines') ||
    variant.includes('ArchieSixes')
  );
};

/**
 * Checks if a given poker variant is a double board game.
 * @param variant The PokerVariant to check.
 * @returns True if the variant is a double board game, false otherwise.
 */
export const isDoubleBoardGame = (variant: string): boolean => {
  if (!variant) return false;

  return variant.includes('DoubleBoard');
};

/**
 * Determines if a poker variant requires two-phase winning hand display.
 * This includes split pot games (high/low) and double board games.
 * @param variant The PokerVariant to check.
 * @returns True if the variant needs extended time for two-phase display, false otherwise.
 */
export const needsTwoPhaseDisplay = (variant: string): boolean => {
  return isSplitPotGame(variant) || isDoubleBoardGame(variant);
};

/**
 * Checks if a given poker variant considers Ace as low for sorting purposes.
 * Note: This is primarily for display sorting. Game rules for Ace high/low may differ.
 * For example, in 2-7 games, Ace is high for hand ranking, but we might still sort A-5 straight low to high visually.
 * Razz games specifically treat Ace as low. Lowball draw games vary. Badugi variants treat Ace as low.
 * @param variant The PokerVariant to check.
 * @returns True if Ace should be treated as low for sorting, false otherwise.
 */
export const isVariantAceLow = (variant: PokerVariant): boolean => {
  const aceLowVariants = [
    PokerVariant.FixedLimitAceToFiveLowballTripleDraw,
    PokerVariant.FixedLimitBadugi,
    PokerVariant.FixedLimitBadacey,
  ];

  return aceLowVariants.includes(variant);
};

/**
 * Which stake fields matter for a given variant (true = relevant).
 */
export interface StakeFields {
  ante: boolean;
  smallBlind: boolean;
  bigBlind: boolean;
  bringIn: boolean;
  smallBet: boolean;
  bigBet: boolean;
}

/**
 * Returns stake-field relevance for the supplied variant.
 */
export const getStakeFieldsForVariant = (variant: string): StakeFields => {
  const { category, bettingStructure } = getGameInfoForVariant(variant);

  // OFC games use points, not traditional betting
  if (category === PokerCategory.Chinese) {
    return {
      ante: false,
      smallBlind: false,
      bigBlind: false,
      bringIn: false,
      smallBet: false,
      bigBet: false,
    };
  }

  const usesBlinds = [BettingStructure.NO_LIMIT, BettingStructure.POT_LIMIT, BettingStructure.MIXED_LIMIT].includes(
    bettingStructure,
  );

  const usesFixedBets = [BettingStructure.FIXED_LIMIT, BettingStructure.MIXED_LIMIT].includes(bettingStructure);

  const isStud = category === PokerCategory.Stud;

  // Base flags (ante is always potentially present)
  const flags: StakeFields = {
    ante: true,
    smallBlind: !isStud && usesBlinds,
    bigBlind: !isStud && usesBlinds,
    bringIn: isStud,
    smallBet: isStud || usesFixedBets,
    bigBet: isStud || usesFixedBets,
  };

  // Special-case: NL 2-7 Single Draw (blinds only, no bring-in or fixed bets)
  if (
    variant === PokerVariant.NoLimitDeuceToSevenLowballSingleDraw ||
    variant === PokerVariant.NoLimitDeuceToSevenLowballSingleDrawCapped
  ) {
    flags.bringIn = flags.smallBet = flags.bigBet = false;
  }

  return flags;
};
